import React from 'react'
import styled from 'styled-components'

import type { WarehouseEffectivePermissions, FunctionRole } from '../../../../types/rbac'
import { ALL_SEGMENTS, SEGMENT_LABELS, LEVEL_LABELS } from '../../../../types/rbac'

interface EffectivePermissionsMatrixProps {
  permissions: WarehouseEffectivePermissions
}

const MatrixContainer = styled.div`
  overflow-x: auto;
  border: 1px solid #ebedf2;
  border-radius: 4px;
`

const MatrixTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9em;

  th,
  td {
    padding: 0.75em 1em;
    text-align: center;
    border: 1px solid #ebedf2;
    white-space: nowrap;
  }

  th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #48465b;
  }

  th:first-child,
  td:first-child {
    text-align: left;
    font-weight: 500;
    background-color: #f8f9fa;
  }

  tbody tr:hover {
    background-color: #f8f9fa;
  }
`

const LevelBadge = styled.span<{ level: FunctionRole; hasOverride?: boolean }>`
  display: inline-block;
  padding: 0.25em 0.75em;
  border-radius: 4px;
  font-weight: 500;
  font-size: 0.85em;
  background-color: ${({ level }) => {
    switch (level) {
      case 'admin':
        return '#f3e5f5' // purple
      case 'operator':
        return '#e8f5e9' // green
      case 'viewer':
        return '#e3f2fd' // blue
      case 'none':
      default:
        return '#f5f5f5' // gray
    }
  }};
  color: ${({ level }) => {
    switch (level) {
      case 'admin':
        return '#7b1fa2' // purple
      case 'operator':
        return '#2e7d32' // green
      case 'viewer':
        return '#1565c0' // blue
      case 'none':
      default:
        return '#9e9e9e' // gray
    }
  }};

  ${({ hasOverride }) =>
    hasOverride &&
    `
    &::after {
      content: '*';
      color: #d32f2f;
      font-weight: bold;
      margin-left: 2px;
    }
  `}
`

export const EffectivePermissionsMatrix = ({ permissions }: EffectivePermissionsMatrixProps) => {
  const scopes = permissions.scopes

  if (scopes.length === 0) {
    return (
      <div
        style={{
          padding: '2em',
          textAlign: 'center',
          background: '#f8f9fa',
          borderRadius: '4px',
          color: '#6c7293',
        }}
      >
        No permissions configured. Assign a role to see effective permissions.
      </div>
    )
  }

  return (
    <MatrixContainer>
      <MatrixTable>
        <thead>
          <tr>
            <th>Segment</th>
            {scopes.map((scope, idx) => (
              <th key={idx}>
                {scope.scope_type === 'global' ? 'Global' : scope.warehouse_name}
                {scope.role && <div style={{ fontSize: '0.8em', fontWeight: 400 }}>({scope.role.name})</div>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ALL_SEGMENTS.map((segment) => (
            <tr key={segment}>
              <td>{SEGMENT_LABELS[segment]}</td>
              {scopes.map((scope, idx) => {
                const segmentPerm = scope.segments[segment]
                const level = segmentPerm?.level || 'none'
                const hasOverride = segmentPerm?.source === 'override'

                return (
                  <td key={idx}>
                    <LevelBadge level={level} hasOverride={hasOverride}>
                      {LEVEL_LABELS[level]}
                    </LevelBadge>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </MatrixTable>
    </MatrixContainer>
  )
}

export default EffectivePermissionsMatrix
